import React, { useEffect, useRef, useState } from "react";
import "./UserDetails.css";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import {
  CalendarIcon,
  CircleMoreIcon,
  DownArrowIcon,
  MailIcon,
  MessageSquareIcon,
  MoviePlayIcon,
  PhoneIcon,
  RegStarIcon,
} from "../../../../icons/reactIcons";
import StatusChip from "../../../../components/custom-statusChip/StatusChip";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchUserList,
  fetchUserDetails,
} from "../../../../redux/slices/userListSlice";

import UserActivity from "./UserActivity";
import WeeklyReportCard from "./WeeklyReportCard";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { ActivityData } from "../../../../apis/userApis";
import Loader from "../../../../components/custom-loader/Loader";
import { useAppSelector } from "../../../../redux/store/hooks";
import { fetchWeekListThunk } from "../../../../redux/slices/lessonSlice";
import { formatDateMMDDYYYY } from "../../../../utils/formatdateMMDDYYYY";
import { getCurrentYear } from "../../../../utils/getCurrentYear";

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const tab = useSelector((state: RootState) => state.userNavigation.tab);
  const { userDetailId } = useParams<{ userDetailId: string }>();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { users, loading, weeklyReport, weeklyReportLoading } = useSelector(
    (state: RootState) => state.userList,
  );
  const { weekList } = useAppSelector((state) => state.lesson);

  const [selectedWeek, setSelectedWeek] = useState<any>(null);
  const [weekDropdownOpen, setWeekDropdownOpen] = useState(false);
  const weekDropdownRef = useRef<HTMLDivElement>(null);
  const currentYear = getCurrentYear();

  /* =====================================================
     FETCH DATA
  ===================================================== */

  useEffect(() => {
    if (weekList.length === 0) {
      dispatch(fetchWeekListThunk());
    }
  }, [dispatch, weekList.length]);

  useEffect(() => {
    if (!userDetailId) return;

    dispatch(
      fetchUserList({
        pageId: 1,
        limit: 30,
        status: tab?.toLowerCase() || "",
      }),
    );

    dispatch(
      fetchUserDetails({
        userId: userDetailId,
        startDate: `${currentYear}-01-01`,
        endDate: `${currentYear}-12-31`,
        ...(selectedWeek && { weekId: selectedWeek }),
      }),
    );
  }, [dispatch, userDetailId]);

  /* =====================================================
     CLICK OUTSIDE DROPDOWN
  ===================================================== */

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        weekDropdownRef.current &&
        !weekDropdownRef.current.contains(event.target as Node)
      ) {
        setWeekDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (weekList?.length > 0 && !selectedWeek) {
      setSelectedWeek(weeklyReport?.[0].weekId);
    }
  }, [weekList]);

  /* =====================================================
     DERIVED DATA
  ===================================================== */

  const currentUser = users.find((user) => user.id === userDetailId);

  const weekData = weeklyReport?.[0];

  const breadcrumbItems = [
    { label: "Users", onClick: () => navigate("/users") },
    {
      label: `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`,
      disabled: true,
    },
  ];

  const currentWeek = weekData?.totalWeeks.filter(
    (val) => val?.weekId === weekData?.weekId,
  );

  console.log("currentWeek", currentWeek);

  const activityData: ActivityData[] = weekData?.activityData || [];

  const date =
    weekData?.userDetails[0]?.enrolledDate || currentUser?.statusUpdatedDate;

  const userEnrollmentDate = new Date(date ?? "");

  const currentWeekIndex = weekList.findIndex(
    (week) => week.id === selectedWeek,
  );

  const currentActiveWeekIndex = React.useMemo(() => {
    if (!userEnrollmentDate || !weekList?.length) return -1;

    const enrolled = new Date(userEnrollmentDate);
    const today = new Date();

    // Remove time to prevent timezone issue
    enrolled.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffInMs = today.getTime() - enrolled.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) return -1;

    const calculatedIndex = Math.floor(diffInDays / 7);

    // Prevent overflow
    return Math.min(calculatedIndex, weekList.length - 1);
  }, [userEnrollmentDate, weekList]);

  /* =====================================================
     LOADING STATE
  ===================================================== */

  if (loading || weeklyReportLoading) {
    return (
      <div className="user-details-container">
        <Loader />
      </div>
    );
  }

  // if (
  //   !weekData ||
  //   !weekData.lessonDetails ||
  //   weekData.lessonDetails.length === 0
  // ) {
  //   return (
  //     <div className="no-data-message">No data available for this week</div>
  //   );
  // }

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <>
      <div className="user-details-container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="user-detail-header">
          <h2 className="page-title">User Details</h2>

          <div className="user-actions">
            <button
              className="user-message-btn"
              onClick={() =>
                navigate(
                  `/users/${currentUser?.firstName} ${currentUser?.lastName}/messages/${userDetailId}`,
                  {
                    state: {
                      studyId: currentUser?.studyId,
                    },
                  },
                )
              }
            >
              <MessageSquareIcon fontSize={"20px"} /> Message
            </button>

            <div className="more-btn" ref={dropdownRef}>
              <button
                className="user-more-btn"
                onClick={() => setIsOpen(!isOpen)}
              >
                More
              </button>

              {isOpen && (
                <ul className="more-btn-options">
                  <li
                    className="more-btn__option"
                    onClick={() =>
                      navigate(
                        `/users/${currentUser?.firstName} ${currentUser?.lastName}/${userDetailId}/feedbacks`,
                      )
                    }
                  >
                    Feedback
                  </li>
                  {/* <li className="more-btn__option">Delete User</li> */}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* ================= USER SUMMARY ================= */}

        <div className="user-summary">
          <div className="user-summary-main">
            <div className="user-summary-left">
              <h3 className="progress-card-user-name">
                {currentUser?.firstName} {currentUser?.lastName}
              </h3>

              <div className="user-info-tags">
                <span className="user-tag">SID: {currentUser?.studyId}</span>

                <span className="user-tag">
                  <MailIcon className="icon" /> {currentUser?.email}
                </span>

                <span className="user-tag">
                  <PhoneIcon className="icon" /> {currentUser?.mobileNumber}
                </span>

                <span className="user-tag">
                  <CalendarIcon className="icon" /> Enrolled On:{" "}
                  {formatDateMMDDYYYY(userEnrollmentDate) || "N/A"}
                </span>

                {currentUser?.group?.name && (
                  <span className="user-tag">{currentUser?.group?.name}</span>
                )}
              </div>
            </div>

            <div className="user-summary-right">
              <div className="day-streak">
                <div
                  style={{
                    display: "flex",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <RegStarIcon className="star-icon" />
                  <span>{weekData?.streakValue || 0}</span>
                </div>
                <div>Day Streak</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}

          <div className="progress-container">
            <div className="progress-label">
              Program Progress
              <div className="week-dropdown" ref={weekDropdownRef}>
                <div
                  className="week-dropdown-selected"
                  onClick={() => setWeekDropdownOpen(!weekDropdownOpen)}
                >
                  <span className="weeks-text">
                    {currentWeekIndex >= 0
                      ? `${currentWeekIndex + 1}/${weekList?.length} Weeks`
                      : `0/${weekList?.length} Weeks`}
                  </span>
                  <DownArrowIcon fontSize={"1.5rem"} />
                </div>

                {weekDropdownOpen && (
                  <div className="week-dropdown-menu">
                    {weekList.map((week: any, index: number) => {
                      const isFutureWeek = index > currentActiveWeekIndex;

                      return (
                        <div
                          key={week.id}
                          className={`week-dropdown-item ${
                            isFutureWeek ? "disabled-week" : ""
                          }`}
                          onClick={() => {
                            if (isFutureWeek) return;

                            setSelectedWeek(week.id);
                            setWeekDropdownOpen(false);

                            dispatch(
                              fetchUserDetails({
                                userId: userDetailId || "",
                                startDate: "2026-02-26",
                                endDate: "2026-01-26",
                                weekId: week.id,
                              }),
                            );
                          }}
                        >
                          {week.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{
                  width: `${currentUser?.progress || 0}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= USER JOURNEY ================= */}

      <div className="user-journey">
        <div className="user-journey-header">
          <h2 className="journey-title">
            {weekData?.weekName} - User Learning Journey
          </h2>
          <p className="journey-progress">
            {weekData?.completedLesson}/{weekData?.totalLesson} Completed
          </p>
        </div>

        <div className="journey-list">
          {weekData?.lessonDetails?.map((lesson, index) => (
            <div key={index} className="journey-card">
              <div className="journey-card-content">
                <div className="journey-card-header">
                  <div
                    className="journey-card-icon"
                    style={{
                      background: "rgba(250, 47, 151, 0.10)",
                    }}
                  >
                    <MoviePlayIcon fontSize={"24px"} color="#FA2F97" />
                  </div>

                  <div className="journey-header-detail">
                    <h3>{lesson.lessonName}</h3>
                  </div>
                </div>
                <div className="tags">
                  {lesson.categories.map((value: any) => (
                    <span key={value?.id} className="tag">
                      {value?.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="status">
                <StatusChip
                  status={lesson.lessonUserStatus ? "Completed" : "Pending"}
                  color={lesson.lessonUserStatus ? "#1B5E20" : "#9D5625"}
                  backgroundcolor={
                    lesson.lessonUserStatus ? "#C8E6C9" : "#ffd7ab"
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =============== WILL INTEGRATE NEXT =============== */}

      <UserActivity activityData={activityData} userId={userDetailId || ""} />

      {/* <UserActivity /> */}

      <WeeklyReportCard
        username={currentUser?.firstName || ""}
        weeklyReports={weekData?.weeklyReport || []}
      />
    </>
  );
};

export default UserDetails;
